// Observe a list of section IDs and return the one currently in view. Used
// by the sidebar sub-nav so the active child highlights as the user
// scrolls — React Router's NavLink only matches pathname, not hash.
//
// Rules:
// - "In view" means the top of the section has crossed a line 30% down
//   the viewport — close enough to what the user is actually reading,
//   far enough below the sticky header that it triggers naturally.
// - When the location pathname changes, we reset to null so the nav
//   doesn't remember a stale hash.
// - If none of the ids are in view yet, returns null (nothing highlighted).

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    setActive(null);
    if (typeof window === "undefined" || ids.length === 0) return;

    // M12.12 — previously this hook captured element refs on first mount
    // and never retried. That races the page content: SidebarNav mounts
    // immediately (outside the report's loading/error states), but the
    // anchor <section id="platforms">/etc nodes only appear after
    // /api/report resolves. If the hook captured the DOM before the
    // sections existed, no scroll event ever highlighted anything.
    //
    // Fix: re-query getElementById on every recompute (cheap, it's a
    // map lookup), and register a MutationObserver that kicks recompute
    // when new nodes appear — covers the report-data-just-arrived case
    // and any future "lazy mount" additions.
    function recompute() {
      const target = window.innerHeight * 0.3;
      let bestId: string | null = null;
      let bestDistance = Infinity;
      let firstFoundId: string | null = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (firstFoundId === null) firstFoundId = id;
        const top = el.getBoundingClientRect().top;
        // If the section has scrolled above the target line, its distance
        // is how far above; if below, positive distance. We want the
        // closest section whose top has passed the target (>= 0).
        const distance = target - top;
        if (distance >= 0 && distance < bestDistance) {
          bestId = id;
          bestDistance = distance;
        }
      }
      // User hasn't scrolled far enough yet: highlight the first section
      // that exists so the sidebar never looks inert.
      if (!bestId) bestId = firstFoundId;
      setActive(bestId);
    }
    recompute();

    let rafScheduled = false;
    function scheduleRecompute() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        rafScheduled = false;
        recompute();
      });
    }
    const observer = new MutationObserver(scheduleRecompute);
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
    // ids is a new array on every render — depend on the joined string
    // so we only re-run when the actual ids change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join("|"), location.pathname]);

  return active;
}
